import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Flex, Type } from "discover.medical.react-component-library";
import { useLocation, useHistory } from "react-router-dom";
import qs from "qs";

import AssistantPlaceholder from "./AssistantPlaceholder";
import AssistantButton from "./AssistantButton";
import { AssistantPopover, AuthErrorMsg } from "./Assistant.styled";
import AssistantFrame from "./AssistantFrame";
import { useStore, useDispatch } from "../../store/assistantStore";
import {
  fetchAssistantUrl,
  handleAssistantAuthenticationFailure,
  setAssistantNavigationData,
  navigateAssistantClear
} from "../../store/assistantStore/actions";
import logAssistantFailure from "../../store/assistantStore/logAssistantFailure";
import AssistantEventService from "./AssistantEventService";
import { EVENT_BUS_EVENTS, useEventBus } from "../../utils/useEventBus";

let assistantEventService;

const Assistant = ({ assistant, toolbarHeight, showLabel }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { pathname, search, hash } = useLocation();
  const history = useHistory();

  const { domain, assistantButton } = assistant;
  const { assistantUrl, hasError, errorMessage, navigation } = store;

  const [isOpen, setIsOpen] = useState(false);
  const [widgetHeight, setWidgetHeight] = useState(660);
  const [isVisible, setIsVisible] = useState(false);
  const [positionTop, setPositionTop] = useState(null);
  const [positionLeft, setPositionLeft] = useState(null);
  const [hasAssistantLoaded, setHasAssistantLoaded] = useState(false);
  const [pendingBroadcast, setPendingBroadcast] = useState(null);

  const mobileWidth = "100vw";
  const tabletWidth = "40rem";
  const desktopWidth = "40rem";
  const toolbarHeightPlusTwoRemsForSpacing = 62;

  const handleAssistantAuthenticationSuccess = () => {
    setHasAssistantLoaded(true);
  };
  const handleAssistantAuthenticationExpiration = () =>
    fetchAssistantUrl(dispatch, pathname);

  const handleAssistantNavigation = (event) =>
    setAssistantNavigationData(dispatch, event);

  const handleAssistantAuthenticationFailureWithDispatch = () => {
    handleAssistantAuthenticationFailure(dispatch);
  };

  useEffect(() => {
    assistantEventService = new AssistantEventService(
      domain,
      handleAssistantAuthenticationExpiration,
      handleAssistantAuthenticationFailureWithDispatch,
      handleAssistantNavigation,
      handleAssistantAuthenticationSuccess,
      logAssistantFailure
    );
    assistantEventService.listenEvent();
    return () => {
      assistantEventService.unlistenEvent();
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      const possibleWidgetHeight =
        window.innerHeight - toolbarHeightPlusTwoRemsForSpacing;
      let theWidgetHeight = 660;
      if (possibleWidgetHeight < theWidgetHeight) {
        theWidgetHeight = possibleWidgetHeight;
      }

      setWidgetHeight(theWidgetHeight);
    }

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [toolbarHeight]);

  const eventBus = useEventBus();
  useEffect(() => {
    const handler = (context) => {
      if (!hasAssistantLoaded) {
        fetchAssistantUrl(dispatch, pathname);
      }

      setIsOpen(true);

      if (context?.openTo) {
        setPendingBroadcast({
          navType: context.openTo,
          input: context.chatInput, //TODO: remove after chatbot is removed from assistant
          ...(context.params ?? {})
        });
      }
    };
    eventBus.on(EVENT_BUS_EVENTS.LAUNCH_ASSISTANT, handler);
    return () => eventBus.detach(EVENT_BUS_EVENTS.LAUNCH_ASSISTANT, handler);
  }, [eventBus, dispatch, hasAssistantLoaded]);

  // Handle legacy CME UI Assistant routes /?watsonLaunch=ivCompatibility  or /?watsonLaunch=drugInteraction
  useEffect(() => {
    if (search) {
      const queryString = search;
      const assistantLaunchQueryString = qs.parse(queryString, {
        ignoreQueryPrefix: true
      }).watsonLaunch;
      if (assistantLaunchQueryString) {
        if (!hasAssistantLoaded) {
          fetchAssistantUrl(dispatch, pathname);
        }
        setIsOpen(true);

        setPendingBroadcast({ navType: assistantLaunchQueryString });
        history.replace("/");
      }
    }
  }, [search, dispatch]);

  useEffect(() => {
    if (hasAssistantLoaded && pendingBroadcast) {
      assistantEventService.broadcastNavToolEvent(pendingBroadcast);
      setPendingBroadcast(null);
    }
  }, [hasAssistantLoaded, pendingBroadcast]);

  useEffect(() => {
    const { isFetching, fullSlug } = navigation;
    const route = fullSlug;
    const currentRoute = `${pathname}${search}${hash}`;
    //if slug exists, navigate to it (if it has changed)
    if (
      !isFetching &&
      hasAssistantLoaded &&
      route &&
      currentRoute.toLowerCase() !== route.toLowerCase()
    ) {
      dispatch(navigateAssistantClear());
      history.push(route);
    }
  }, [hasAssistantLoaded, navigation, pathname, search, hash, history]);

  return (
    <>
      <AssistantPopover
        open={isOpen}
        placement="top"
        padding={0}
        popperComponentProps={{
          variant: "primary",
          componentClassName: "AssistantPopper"
        }}
        trigger={
          <AssistantButton
            assistantButton={assistantButton}
            showLabel={showLabel}
          />
        }
        data-testid="assistant-popover"
        onOpen={() => {
          // Only called on trigger click
          if (!hasAssistantLoaded) {
            fetchAssistantUrl(dispatch, pathname);
          }
          setIsOpen(true);
        }}
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <Flex
          container
          column
          align={{ content: "center", item: "center" }}
          justify={{ content: "space-between" }}
        >
          <Flex cell span={12}>
            <AssistantPlaceholder
              height={widgetHeight}
              mobileWidth={mobileWidth}
              tabletWidth={tabletWidth}
              desktopWidth={desktopWidth}
              showAssistant={(ref) => {
                const rect = ref.current.getBoundingClientRect();
                setIsVisible(true);
                setPositionTop(`${rect.top}px`);
                setPositionLeft(`${rect.left}px`);
              }}
              hideAssistant={() => {
                setIsVisible(false);
                setPositionTop(null);
                setPositionLeft(null);
              }}
            >
              <>
                {hasError && errorMessage && (
                  <AuthErrorMsg container column align={{ item: "flex-start" }}>
                    <Type element="p" marginBottom="md">
                      {errorMessage}
                    </Type>
                    <Button
                      onClick={() => {
                        fetchAssistantUrl(dispatch, pathname);
                      }}
                    >
                      Retry
                    </Button>
                  </AuthErrorMsg>
                )}
              </>
            </AssistantPlaceholder>
          </Flex>
        </Flex>
      </AssistantPopover>

      {assistantUrl && (
        <AssistantFrame
          assistantUrl={assistantUrl}
          height={widgetHeight}
          mobileWidth={mobileWidth}
          tabletWidth={tabletWidth}
          desktopWidth={desktopWidth}
          isVisible={isVisible}
          positionTop={positionTop}
          positionLeft={positionLeft}
        />
      )}
    </>
  );
};

Assistant.propTypes = {
  assistant: PropTypes.shape({
    assistantButton: PropTypes.object,
    domain: PropTypes.string
  }),
  showLabel: PropTypes.bool,
  toolbarHeight: PropTypes.number
};

Assistant.displayName = "Assistant";
export default Assistant;
